/** @format */

const toString = Object.prototype.toString;

export function is(val: unknown, type: string) {
	return toString.call(val) === `[object ${type}]`;
}

export function isDef<T = unknown>(val?: T): val is T {
	return typeof val !== 'undefined';
}

export function isUnDef<T = unknown>(val?: T): val is T {
	return !isDef(val);
}

export function isObject(val: any): val is Record<any, any> {
	return val !== null && is(val, 'Object');
}

export function isEmpty<T = unknown>(val: T): val is T {
	if (isArray(val) || isString(val)) {
		return val.length === 0;
	}

	if (val instanceof Map || val instanceof Set) {
		return val.size === 0;
	}

	if (isObject(val)) {
		return Object.keys(val).length === 0;
	}

	return false;
}

export function isDate(val: unknown): val is Date {
	return is(val, 'Date');
}

export function isNull(val: unknown): val is null {
	return val === null;
}

export function isNullAndUnDef(val: unknown): val is null | undefined {
	return isUnDef(val) && isNull(val);
}

export function isNullOrUnDef(val: unknown): val is null | undefined {
	return isUnDef(val) || isNull(val);
}

export function isNumber(val: unknown): val is number {
	return is(val, 'Number');
}

export function isPromise<T = any>(val: unknown): val is Promise<T> {
	return is(val, 'Promise') && isObject(val) && isFunction(val.then) && isFunction(val.catch);
}

export function isString(val: unknown): val is string {
	return is(val, 'String');
}

export function isFunction(val: unknown): val is Function {
	return typeof val === 'function';
}

export function isBoolean(val: unknown): val is boolean {
	return is(val, 'Boolean');
}

export function isRegExp(val: unknown): val is RegExp {
	return is(val, 'RegExp');
}

export function isArray(val: any): val is Array<any> {
	return val && Array.isArray(val);
}

export function isWindow(val: any): val is Window {
	return typeof window !== 'undefined' && is(val, 'Window');
}

export function isElement(val: unknown): val is Element {
	return isObject(val) && !!val.tagName;
}

export function isMap(val: unknown): val is Map<any, any> {
	return is(val, 'Map');
}

export const isServer = typeof window === 'undefined';

export const isClient = !isServer;

export function isUrl(path: string): boolean {
	const reg =
		/(((^https?:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+(?::\d+)?|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)$/;
	return reg.test(path);
}

/**
 * 判断是否是手机端
 * @returns {boolean} isMobile
 */
export function isMobile() {
	const userAgentInfo = navigator.userAgent;
	const mobileAgents = ['Android', 'iPhone', 'SymbianOS', 'Windows Phone', 'iPad', 'iPod'];
	let isMobile = false;
	for (let i = 0; i < mobileAgents.length; i++) {
		if (userAgentInfo.indexOf(mobileAgents[i]) > -1) {
			isMobile = true;
			break;
		}
	}
	return isMobile;
}

/**
 * 判断文件扩展名对应类型是否为图片
 * @param {*} str 文件后缀名 png | jpg | ...
 */
export function isImageTypeResExtension(str) {
	// toLowerCase() 将字符串转换为小写，返回一个新的字符串
	return ['png', 'jpg', 'jpeg', 'bmp', 'gif', 'webp', 'psd', 'svg', 'tiff'].indexOf(str.toLowerCase()) !== -1;
}

/**
 * 判断文件路径对应类型是否为图片
 * @param {*} resPath 文件路径 xxx.png | xxx.jpg | ...
 */
export function isImageTypeResPath(resPath) {
	const index = resPath.lastIndexOf('.'); // 获取指定字符串最后一次出现的位置，返回index
	const str = resPath.substr(index + 1); // substr(start, length) 抽取从start下标开始的length个字符，返回新的字符串
	return isImageTypeResExtension(str);
}
